import { Checkbox } from '@ark-ui/react/checkbox';
import { Square, SquareCheck } from 'lucide-react';
import { type FC } from 'react';

/** The weight the design system draws its lucide icons at. */
const ICON_STROKE_WIDTH = 1.75;

/*
 * Ark's Checkbox: the design system exports no checkbox, and the Findings
 * page's comes from @consigli/facade, the stack this modal moves away from.
 *
 * The design draws the two states as whole glyphs - `Icon / square` and
 * `Icon / square-check` - rather than a box with a tick inside it, so both are
 * rendered and swapped on the control's `data-state`. `group` goes on the
 * control because that is the element Ark stamps the state onto.
 *
 * Their colours are the design's own: neutral #57534E unticked, and the dark
 * brand green ticked, which is the one branded mark in the modal besides the
 * label pill. Taken from the exported glyphs rather than assumed - both had
 * been left on the near-black icon colour.
 */
export const AddContextCheckboxControl: FC = () => (
  <Checkbox.Control className="add-context-check group flex size-[var(--ds-space-4)] shrink-0 items-center justify-center">
    <Square
      aria-hidden="true"
      strokeWidth={ICON_STROKE_WIDTH}
      className="size-[var(--ds-space-4)] text-[var(--ds-color-raw-stone-600)] group-data-[state=checked]:hidden"
    />
    <SquareCheck
      aria-hidden="true"
      strokeWidth={ICON_STROKE_WIDTH}
      className="add-context-check-on hidden size-[var(--ds-space-4)] group-data-[state=checked]:block"
    />
  </Checkbox.Control>
);

interface AddContextCheckboxProps {
  checked: boolean;
  /** Rendered beside the box; the design shows it for "Select All". */
  label: string;
  onCheckedChange: (checked: boolean) => void;
}

/** Box plus visible label, for the filter row's Select All. */
export const AddContextCheckbox: FC<AddContextCheckboxProps> = ({
  checked,
  label,
  onCheckedChange,
}) => (
  <Checkbox.Root
    checked={checked}
    onCheckedChange={({ checked: next }) => onCheckedChange(next === true)}
    className="add-context-checkbox flex shrink-0 cursor-pointer items-center gap-[var(--ds-space-2)]"
  >
    <Checkbox.HiddenInput />
    <AddContextCheckboxControl />
    <Checkbox.Label className="whitespace-nowrap text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-secondary)]">
      {label}
    </Checkbox.Label>
  </Checkbox.Root>
);
